/**
 * Placeholder substitution engine for stored document templates.
 *
 * Substitutes `{{path.to.field}}` tokens in a template string with values
 * resolved from a context object. Pure function — no external state, no I/O.
 *
 * See `.kiro/specs/document-template-management/requirements.md` §5 for the
 * full behavioural contract. Summary:
 *
 * - Token grammar: `{{` + identifier-path + `}}`, where identifier-path is
 *   one or more dot-separated identifiers matching
 *   `[a-zA-Z_][a-zA-Z0-9_]*`. Anything that does not match the grammar —
 *   including pipes (`{{x | currency}}`), spaces, conditionals, loops — is
 *   left untouched in the output.
 * - Coercion matrix:
 *     string / number        → `String(value)`
 *     true                   → `'true'`
 *     false                  → `'false'`
 *     null / undefined / missing path segment → `''`
 *     object (non-null) / array → `''` + `console.warn` once per unique path
 * - Deterministic: same `(template, structurally-equal context)` → same
 *   output string.
 * - Does NOT support filters, pipes, conditionals, or loops.
 */

const TOKEN_REGEX =
  /\{\{([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*)\}\}/g;

/**
 * Traverse `context` by splitting `path` on `.`. Returns `undefined` whenever
 * the chain hits a non-object intermediate or a missing key — the caller
 * treats `undefined` the same as a null/missing leaf (coerced to `''`).
 */
function resolvePath(context: Record<string, any>, path: string): unknown {
  const segments = path.split('.');
  let cursor: unknown = context;
  for (const segment of segments) {
    if (cursor === null || typeof cursor !== 'object') {
      return undefined;
    }
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

/**
 * Apply the value-coercion matrix. `warnedPaths` is mutated so we emit at
 * most one object/array warning per unique path per `renderTemplate` call.
 */
function coerce(value: unknown, path: string, warnedPaths: Set<string>): string {
  if (value === null || value === undefined) {
    return '';
  }
  const kind = typeof value;
  if (kind === 'string' || kind === 'number') {
    return String(value);
  }
  if (kind === 'boolean') {
    return value ? 'true' : 'false';
  }
  // Anything else is object-shaped (including arrays). Substitute empty and
  // warn at most once per path.
  if (kind === 'object') {
    if (!warnedPaths.has(path)) {
      warnedPaths.add(path);
      // eslint-disable-next-line no-console
      console.warn(
        '[templateRenderer] path %s resolved to object/array; substituting empty',
        path
      );
    }
    return '';
  }
  // Unreachable in practice (functions, symbols, bigints are not produced by
  // the catalog/fixture), but keep the fall-through safe.
  return '';
}

/**
 * Substitute `{{path}}` tokens in `template` using values from `context`.
 *
 * @param template The raw template string with zero or more `{{path}}` tokens.
 * @param context  The `{ campaign, match, influencer, host, brand }`-shaped
 *                 object whose leaves feed the substitution.
 * @returns The rendered string.
 */
export function renderTemplate(
  template: string,
  context: Record<string, any>
): string {
  // Fast path: templates without `{{` have no tokens to substitute. Returning
  // the original reference guarantees byte-for-byte identity (Req 13.1) and
  // avoids regex allocation for the common no-token case.
  if (template.indexOf('{{') === -1) {
    return template;
  }

  // Scoped per call so concurrent renders don't share each other's warning
  // budget (Req 5.6 — "once per unique path per render").
  const warnedPaths = new Set<string>();

  return template.replace(TOKEN_REGEX, (_match, path: string) =>
    coerce(resolvePath(context, path), path, warnedPaths)
  );
}
